/* eslint-disable @next/next/no-img-element */
import PropTypes from 'prop-types';
import Button from '@base/Button/Button';
import H1 from '@base/H1/H1';
import H4 from '@base/H4/H4';
import { mobile } from '@styles/index';
import { getImage } from '@utils/imageHelper';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { useIsMobile } from '@stores/AppCommonData';
import { COMBAT_LEARN_MORE, COMBAT_WEB_ENROLL } from '@constants/segment';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import { getTopologyDataByUID } from '@selectors/topologies';
import useCombatSegmentAnalytics from '../Common/useCombatSegmentAnalytics';

const Wrapper = styled.div`
  margin-top: 72px;
  position: relative;
  margin-bottom: 30px;
`;

const BgImage = styled.img`
  position: absolute;
  z-index: -1;
  width: 100%;
  left: 0;
  right: 0;
  max-width: 1366px;
  margin: auto;
  @media only screen and (max-width: ${mobile.maxWidth}) {
    display: none;
    max-width: unset;
  }
`;

const BgImageMobile = styled.img`
  @media not all and (max-width: ${mobile.maxWidth}) {
    display: none;
  }
  @media only screen and (max-width: ${mobile.maxWidth}) {
    position: relative;
    height: auto;
    width: 100%;
  }
`;

const ContentContainer = styled.div`
  max-width: 1136px;
  margin: auto;
  @media only screen and (max-width: ${mobile.maxWidth}) {
    width: 100%;
  }
`;

const Content = styled.div`
  padding-top: 86px;
  max-width: 580px;
  @media only screen and (max-width: ${mobile.maxWidth}) {
    margin: 24px auto;
    text-align: center;
    max-width: 300px;
    padding-top: 40px;
  }
  h1 {
    font-size: 40px;
    margin-bottom: 24px;
    line-height: 120%;
    @media only screen and (max-width: ${mobile.maxWidth}) {
      font-size: 24px;
      margin-bottom: 16px;
    }
  }
  h4 {
    font-weight: normal;
    color: var(--color-text-secondary);
    @media only screen and (max-width: ${mobile.maxWidth}) {
      font-size: 14px;
    }
  }
`;

const Logo = styled.img`
  height: 90px;
  margin-left: -8px;
  margin-bottom: 24px;
  @media only screen and (max-width: ${mobile.maxWidth}) {
    height: 60px;
  }
`;

const ButtonsWrapper = styled.div`
  display: flex;
  margin-top: 24px;
  margin-bottom: 24px;
  @media only screen and (max-width: ${mobile.maxWidth}) {
    justify-content: center;
  }
  button:first-child {
    margin-right: 16px;
  }
`;

const PromotionalLine = styled.div`
  display: flex;
  align-items: center;
  @media only screen and (max-width: ${mobile.maxWidth}) {
    justify-content: center;
  }
  h4 {
    font-weight: normal;
    color: var(--color-i-black);
    margin-left: 8px;
  }
`;

const HeaderAsset = styled.img`
  margin: auto;
  display: block;
  margin-top: 280px;
  @media only screen and (max-width: ${mobile.maxWidth}) {
    position: relative;
    top: 0;
    left: 0;
    width: 100%;
    height: 210px;
    object-fit: cover;
    margin-top: 0px;
  }
`;

const CombatsHeader = ({ goalUID, onEnroll, onLearnMore }) => {
  const data = useSelector(({ combats }) => combats.staticData[goalUID] || {});
  const combatsInfo = useSelector(
    ({ combats }) => combats.upcoming?.[goalUID] || {}
  );
  const isEnrolled = combatsInfo.results?.some((combat) => combat.is_enrolled);
  const isMobile = useIsMobile();
  const segmentAnalytics = useCombatSegmentAnalytics();
  const goal = useSelectorWithShallowEqual(getTopologyDataByUID(goalUID));

  const onEnrollClick = () => {
    onEnroll?.();
    if (!isEnrolled) {
      segmentAnalytics(COMBAT_WEB_ENROLL, {
        combat_user_level: 'NA',
        combat_user_state: 'NA',
        goal_name: goal?.name ?? 'NA',
        is_enrolled: isEnrolled,
        last_primary_source_section: 'NA'
      });
    }
  };
  const onLearnMoreClicked = () => {
    onLearnMore?.();
    segmentAnalytics(COMBAT_LEARN_MORE, {
      combat_class: 'NA',
      combat_user_level: 'NA',
      combat_user_state: 'NA',
      goal_name: goal?.name ?? 'NA',
      is_enrolled: isEnrolled,
      last_primary_source_section: 'NA'
    });
  };

  return (
    <Wrapper>
      {!isMobile && (
        <BgImage src={getImage('combat/castle.svg')} alt="castle" />
      )}
      <ContentContainer>
        <Content>
          <Logo src={data.logo} alt="logo wordmark" />
          <H1>{data.heading}</H1>
          <H4>{data.subheading}</H4>
          <ButtonsWrapper>
            <Button
              grid={3}
              label={isEnrolled ? 'Enrolled' : 'Enroll now'}
              color={isEnrolled ? 'black' : 'blue'}
              variant={isEnrolled ? 'hollow' : 'filled'}
              onClick={onEnrollClick}
            />
            <Button
              grid={3}
              label="Learn more"
              color="black"
              variant="hollow"
              onClick={onLearnMoreClicked}
            />
          </ButtonsWrapper>
          <PromotionalLine>
            <img src={data.promoTag} alt="new tag" />
            <H4>{data.promotionalLine}</H4>
          </PromotionalLine>
        </Content>
      </ContentContainer>
      {isMobile && (
        <BgImageMobile
          src={getImage('combat/top-banner-mobile.svg')}
          alt="castle"
        />
      )}
      <HeaderAsset src={data.headerAsset} alt="header-asset" />
    </Wrapper>
  );
};

export default CombatsHeader;

CombatsHeader.propTypes = {
  goalUID: PropTypes.string.isRequired,
  onEnroll: PropTypes.func.isRequired,
  onLearnMore: PropTypes.func.isRequired
};
